import styles from './styles.module.css';
import React, { useState, useContext, useEffect } from 'react';
import { AlertMessagesContext } from 'react-alert-messages';
import DataTable from 'components/utils/DataTable/index';
import { Button, Tooltip, Grid } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { UserContext } from 'Context/UserContext';
import { ShopContext } from 'Context/ShopContext';
import { SUBSCRIPTION_TYPE, USER_PERMISSIONS } from 'const';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import Info from 'components/utils/Alert/Info';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import PriceCategoryService from 'services/PriceCategoryService';
import PriceCategoryFormDialog from 'components/popups/PriceCategoryFormDialog';
import PriceCategoryEditDialog from 'components/popups/PriceCategoryEditDialog';
import { InventoryContext } from 'Context/InventoryContext';
import { useTranslation } from 'react-i18next';

function PriceCategory() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { user } = useContext(UserContext);
  const { shop } = useContext(ShopContext);
  const { getPriceCategories, priceCategories, setPriceCategories } = useContext(InventoryContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [selectedForEdit, setSelectedForEdit] = useState(null);
  const [selectedForDelete, setSelectedForDelete] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedForForceDelete, setSelectedForForceDelete] = useState(null);

  const subscriptionType = shop?.subscription?.type;
  const userHasSalesPersonWritePermission = user?.shop_permissions?.sales_persons_permission === USER_PERMISSIONS.WRITE;

  const handleEditClick = (item) => setSelectedForEdit(item);
  const handleAddClick = () => setShowAddForm(true);
  const handleDeleteClick = (item) => setSelectedForDelete(item);

  const handleDelete = async (category, force = false) => {
    setLoadingIndicator(true);
    try {
      await PriceCategoryService.deletePriceCategory(category.id, force ? { force: true } : {});
      setPriceCategories((prev) => prev.filter((c) => c.id !== category.id));
      postAlertMessage({ text: tManagement('Price category deleted successfully'), type: 'success' });
      setSelectedForDelete(null);
      setSelectedForForceDelete(null);
    } catch (error) {
      if (error.message === 'RESOURCE_IS_REFERENCED' && !force) {
        setSelectedForForceDelete(category);
      } else {
        postAlertMessage({ text: error?.message, type: 'failed' });
      }
    }
    setLoadingIndicator(false);
  };

  const headerData = [
    {
      label: tCommon('Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Actions'),
      id: 'toggleBtn',
      type: 'callback',
      align: 'center',
      viewRender: (item) => {
        return (
          <Grid className={styles.btnGroupCenter}>
            <>
              <Tooltip title={tCommon('Edit Category')} onClick={() => handleEditClick(item)}>
                <EditIcon className={styles.iconButton} />
              </Tooltip>
              <Tooltip title={tCommon('Delete category')} onClick={() => handleDeleteClick(item)}>
                <DeleteIcon className={styles.iconButton} />
              </Tooltip>
            </>
          </Grid>
        );
      },
    },
  ];

  useEffect(() => {
    getPriceCategories();
  }, [getPriceCategories]);

  return (
    <>
      <div className={styles.contentLayout}>
        <div className={styles.settingsGrid}>
          <div className={styles.BillHeaderDiv}>
            <span className={styles.pageHead}>{tCommon('Manage Price Categories')}</span>
            <div className={styles.filerInputSec}>
              {subscriptionType &&
                subscriptionType === SUBSCRIPTION_TYPE.PREMIUM &&
                userHasSalesPersonWritePermission && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ backgroundColor: '#ff851b' }}
                    className={styles.actionBtn}
                    onClick={handleAddClick}
                  >
                    <NoteAddIcon className={styles.actionBtnIcon} />
                    {tCommon('New Category')}
                  </Button>
                )}
            </div>
          </div>
          {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.PREMIUM ? (
            <RestrictionInfo
              title={tManagement('Price Category feature not available')}
              content={tManagement('To get Price Category support options, contact support team.')}
            />
          ) : (
            (priceCategories !== null || loadingIndicator || priceCategories === null) &&
            ((priceCategories && priceCategories.length > 0) || loadingIndicator || priceCategories === null ? (
              <DataTable
                columns={headerData}
                rows={priceCategories || []}
                isLoading={loadingIndicator || priceCategories === null}
              />
            ) : (
              !loadingIndicator && (
                <Info
                  title={tManagement('No price categories to list')}
                  content={tManagement('You have no price categories to list. Please create a new table')}
                />
              )
            ))
          )}
        </div>
      </div>
      {showAddForm && (
        <PriceCategoryFormDialog
          handleClose={() => setShowAddForm(false)}
          handleSuccess={(newCategory) => {
            setPriceCategories((prev) => [...prev, newCategory]);
            setShowAddForm(false);
          }}
        />
      )}
      {selectedForDelete && (
        <ConfirmPopup
          data={selectedForDelete}
          handleClose={() => setSelectedForDelete(null)}
          handleOkay={() => handleDelete(selectedForDelete)}
          alertTitle={tManagement('Confirm delete')}
          successBtnName={tCommon('Delete')}
          alertContent={tManagement('Deleted price category can\'t be restored. Do you want to proceed?')}
          isLoading={loadingIndicator}
        />
      )}

      {selectedForForceDelete && (
        <ConfirmPopup
          data={selectedForForceDelete}
          handleClose={() => setSelectedForForceDelete(null)}
          handleOkay={() => handleDelete(selectedForForceDelete, true)}
          alertTitle={tManagement('Confirm force delete')}
          successBtnName={tCommon('Delete')}
          alertContent={tManagement('Price category {{name}} is linked to some items. Deleting will remove it from those items. Proceed?', { name: selectedForForceDelete?.name })}
          isLoading={loadingIndicator}
        />
      )}
      {selectedForEdit && (
        <PriceCategoryEditDialog
          priceCategory={selectedForEdit}
          handleClose={() => {
            setSelectedForEdit(null);
          }}
          handleSuccess={(updatedCategory) => {
            setSelectedForEdit(null);
            const _priceCategories = priceCategories.map((category) =>
              category.id === selectedForEdit.id ? updatedCategory : category
            );
            setPriceCategories(_priceCategories);
          }}
        />
      )}
    </>
  );
}

export default PriceCategory;
